"use client";

import { useEffect, useState } from "react";
import SponsoredBanners from "../ui/SponsoredBanners";
import ModalCloseButton from "../ui/ModalCloseButton";
import ModalPortal from "../ui/ModalPortal";
import { useSponsoredBanners } from "../providers/SponsoredBannersProvider";
import { useEscapeKey } from "@shared/lib/use-escape-key";
import { useScrollLock } from "@/lib/use-scroll-lock";

// Helper functions to work with cookies
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

function setCookie(name: string, value: string, days: number = 365) {
  if (typeof document === 'undefined') return;
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
}

export default function BannerSlot5Modal() {
  const [isVisible, setIsVisible] = useState(false);
  const { banners, loading } = useSponsoredBanners();

  useEffect(() => {
    // Don't show if still loading or if no banners exist for position 5
    if (loading) return;
    
    // Check if there are any banners for position 5
    // The API endpoint /api/sponsored-banners/active only returns active banners
    const hasBannersForPosition5 = banners.some(
      (banner) => banner.position === "5"
    );
    
    if (!hasBannersForPosition5) {
      return; // Don't show modal if no banners configured for position 5
    }

    // Check if the user has already seen/dismissed the banner using cookie
    const hasSeenBanner = getCookie("banner5Dismissed");
    
    if (!hasSeenBanner) {
      // Show the banner after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [banners, loading]);

  useScrollLock(isVisible);

  const handleClose = () => {
    setIsVisible(false);
    // Set cookie to remember dismissal (expires in 1 year)
    setCookie("banner5Dismissed", "true", 365);
  };
  useEscapeKey(isVisible, handleClose);

  if (!isVisible) {
    return null;
  }

  return (
    <ModalPortal>
      {/* Overlay Background */}
      <div
        className="fixed inset-0 bg-[#07063CBF] z-[999]"
        onClick={handleClose}
        aria-label="Close banner overlay"
      />

      {/* Banner Container */}
      <div className="fixed bottom-8 left-0 right-0 px-4 sm:px-6 lg:px-8 z-[1000]">
        <div className="relative">
          {/* Close Button */}
          <ModalCloseButton
            onClick={handleClose}
            className="absolute right-5 top-5 z-[1001]"
            aria-label="Close banner"
          />

          {/* Banner Content */}
          <SponsoredBanners position="5" className="w-full" />
        </div>
      </div>
    </ModalPortal>
  );
}
